import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileText, Paperclip } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import inductionService from '../../services/induction';
import type { PolicyForReading } from '../../types/induction';
import { formatDateTimeInTz, formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';

function errorCode(err: unknown): string | undefined {
  return (err as { code?: string } | undefined)?.code;
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/**
 * One company policy, to read and acknowledge — or to re-read any time.
 * Acknowledgement is against the version on screen; a STALE_VERSION refusal
 * means the text changed while it was open, so it reloads and asks again.
 */
export default function ReadPolicy() {
  const { id } = useParams<{ id: string }>();
  const policyId = Number(id);
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const queryClient = useQueryClient();

  const [policy, setPolicy] = useState<PolicyForReading | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [nextId, setNextId] = useState<number | null>(null);
  const [justAcknowledged, setJustAcknowledged] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPolicy(await inductionService.readPolicy(policyId));
    } catch (err) {
      setError(errorMessage(err, 'Could not load this policy.'));
    } finally {
      setLoading(false);
    }
  }, [policyId]);

  useEffect(() => {
    setAgreed(false);
    setJustAcknowledged(false);
    setNotice(null);
    void load();
  }, [load]);

  const handleAcknowledge = async () => {
    if (!policy || !agreed) return;
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      await inductionService.acknowledge('policy_document', policy.id, policy.ack_version);
      // InductionGate caches the pack for minutes; without this it re-prompts
      // for the policy just acknowledged the moment the reader leaves this page.
      void queryClient.invalidateQueries({ queryKey: ['inductions', 'mine'] });
      setJustAcknowledged(true);
      await load();
      try {
        const mine = await inductionService.mine();
        const next = mine.pack.find((p) => p.status !== 'acknowledged' && p.id !== policy.id);
        setNextId(next ? next.id : null);
      } catch {
        setNextId(null);
      }
    } catch (err) {
      if (errorCode(err) === 'STALE_VERSION') {
        setAgreed(false);
        setNotice('This policy was updated while you were reading it. The new version is shown below — please read it again before acknowledging.');
        await load();
      } else {
        setError(errorMessage(err, 'Could not record your acknowledgement. Please try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const acknowledged = policy?.status === 'acknowledged';
  const meta = policy
    ? [policy.reference_code, policy.version_label, policy.effective_date ? `Effective ${formatLocalDateInTz(policy.effective_date, orgTz)}` : null]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <>
      <PageHeader
        title={policy?.title || 'Company policy'}
        subtitle={meta || undefined}
        icon={<FileText className="h-6 w-6 text-white" />}
      />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {notice && (
          <div className="bg-warning-50 border border-warning-200 rounded-lg px-4 py-3 text-sm text-warning-800 break-words">{notice}</div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>
        )}

        {loading && !policy ? (
          <div className="h-64 bg-neutral-100 rounded-xl animate-pulse" />
        ) : policy && (
          <>
            {policy.status === 'updated' && !notice && (
              <div className="bg-warning-50 border border-warning-200 rounded-lg px-4 py-3 text-sm text-warning-800 break-words">
                This policy has been updated since you last acknowledged it. Please read it again.
              </div>
            )}

            <article className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6 min-w-0">
              <h2 className="text-xl font-semibold text-neutral-900 break-words">{policy.title}</h2>
              {meta && <p className="mt-1 text-xs text-neutral-500 break-words">{meta}</p>}
              {policy.description && <p className="mt-3 text-sm text-neutral-600 break-words">{policy.description}</p>}
              <div className="mt-4">
                {policy.content
                  ? <PolicyMarkdown text={policy.content} />
                  : !policy.file?.url && <p className="text-sm text-neutral-500">This policy has no written content.</p>}
              </div>
              {policy.file?.url && (
                <a
                  href={policy.file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex max-w-full items-center gap-2 rounded-lg border border-neutral-200 px-3 py-2 text-sm text-primary-700 hover:bg-neutral-50"
                >
                  <Paperclip className="h-4 w-4 shrink-0" />
                  <span className="break-all">{policy.file.name || 'Open the attached document'}</span>
                </a>
              )}
            </article>

            <section className="bg-neutral-50 border border-neutral-200 rounded-xl p-4 sm:p-5 space-y-3">
              {acknowledged ? (
                <>
                  <p className="flex items-start gap-2 text-sm font-medium text-success-700 break-words">
                    <CheckCircle2 className="h-5 w-5 shrink-0" />
                    <span className="min-w-0">
                      Acknowledged {policy.acknowledged_at ? formatDateTimeInTz(policy.acknowledged_at, orgTz) : ''}
                    </span>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {justAcknowledged && nextId !== null ? (
                      <Button onClick={() => navigate(`/my-inductions/policies/${nextId}`)}>Next policy</Button>
                    ) : null}
                    <Button variant={justAcknowledged && nextId !== null ? 'outline' : 'primary'} onClick={() => navigate('/my-inductions')}>
                      Back to my inductions
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-neutral-700">I have read and understood this policy</span>
                  </label>
                  <p className="text-xs text-neutral-500">The date and time you acknowledge it are recorded.</p>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={handleAcknowledge} disabled={!agreed || submitting} isLoading={submitting}>
                      Acknowledge
                    </Button>
                    <Button variant="outline" onClick={() => navigate('/my-inductions')} disabled={submitting}>
                      Later
                    </Button>
                  </div>
                </>
              )}
            </section>
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// A deliberately tiny markdown renderer: headings, bullet and numbered lists,
// paragraphs, a --- divider, **bold** and *italic*. Everything goes through
// React text nodes — no dangerouslySetInnerHTML — so a policy can never inject
// markup.
// ---------------------------------------------------------------------------

function inline(text: string): ReactNode[] {
  // Italic must open and close on a non-space, so "5 * 3 * 2" stays arithmetic.
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*\s](?:[^*]*[^*\s])?\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**') && p.length > 4) {
      return <strong key={i} className="font-semibold text-neutral-900">{p.slice(2, -2)}</strong>;
    }
    if (p.length > 2 && p.startsWith('*') && p.endsWith('*')) {
      return <em key={i}>{p.slice(1, -1)}</em>;
    }
    return <span key={i}>{p}</span>;
  });
}

type Block =
  | { kind: 'hr' }
  | { kind: 'h'; level: number; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] }
  | { kind: 'p'; text: string };

function parse(md: string): Block[] {
  const blocks: Block[] = [];
  let para: string[] = [];
  const flush = () => {
    if (para.length) blocks.push({ kind: 'p', text: para.join('\n') });
    para = [];
  };
  for (const raw of md.replace(/\r\n/g, '\n').split('\n')) {
    const line = raw.trimEnd();
    const h = /^(#{1,6})\s+(.*)$/.exec(line.trim());
    const ul = /^\s*[-*•]\s+(.*)$/.exec(line);
    const ol = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (!line.trim()) { flush(); continue; }
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) { flush(); blocks.push({ kind: 'hr' }); continue; }
    if (h) { flush(); blocks.push({ kind: 'h', level: h[1].length, text: h[2] }); continue; }
    if (ul || ol) {
      flush();
      const kind = ul ? 'ul' : 'ol';
      const text = (ul ?? ol)![1];
      const last = blocks[blocks.length - 1];
      if (last && last.kind === kind) last.items.push(text);
      else blocks.push({ kind, items: [text] });
      continue;
    }
    para.push(line.trim());
  }
  flush();
  return blocks;
}

export function PolicyMarkdown({ text }: { text: string }) {
  const blocks = parse(text);
  return (
    <div className="space-y-3 text-sm text-neutral-700 leading-relaxed break-words min-w-0">
      {blocks.map((b, i) => {
        if (b.kind === 'hr') return <hr key={i} className="border-neutral-200" />;
        if (b.kind === 'h') {
          const cls = b.level <= 1
            ? 'text-lg font-semibold text-neutral-900 pt-2'
            : b.level === 2
              ? 'text-base font-semibold text-neutral-900 pt-2'
              : 'text-sm font-semibold text-neutral-900 pt-1';
          return <h3 key={i} className={cls}>{inline(b.text)}</h3>;
        }
        if (b.kind === 'ul') {
          return <ul key={i} className="list-disc pl-5 space-y-1">{b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}</ul>;
        }
        if (b.kind === 'ol') {
          return <ol key={i} className="list-decimal pl-5 space-y-1">{b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}</ol>;
        }
        return <p key={i} className="whitespace-pre-line">{inline(b.text)}</p>;
      })}
    </div>
  );
}
